import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Download, Upload, FileSpreadsheet, Loader2, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Master-Catalogue Bulk Status Update
 * -----------------------------------
 * Lets admin/manager upload a CSV/XLSX file with `Product ID · Product Name ·
 * Status` and flip every listed SKU active/inactive in one shot. Surfaces a
 * per-row outcome table so users can immediately see which rows succeeded,
 * which were skipped (already at target status), and which failed.
 */
const ProductBulkStatusDialog = ({ open, onOpenChange, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API}/products/bulk-status-template.xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'product-status-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Template download failed');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Select a file first');
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${API}/products/bulk-status-update`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setResult(resp.data);
      if (resp.data.success_count > 0) {
        toast.success(`Updated ${resp.data.success_count} SKU status(es)`);
        onSuccess?.();
      } else if (resp.data.error_count > 0) {
        toast.error(`${resp.data.error_count} row(s) failed`);
      } else {
        toast(`${resp.data.skipped_count} row(s) already at target status — nothing changed`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk update failed');
    } finally {
      setUploading(false);
    }
  };

  const close = () => {
    setFile(null);
    setResult(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-status-dialog">
        <DialogHeader>
          <DialogTitle>Bulk SKU Status Update</DialogTitle>
          <DialogDescription>
            Upload a CSV / XLSX containing <b>Product ID</b>, <b>Product Name</b>, and{' '}
            <b>Status</b> (Active / Inactive). The system updates every listed SKU in one
            pass and gives a per-row outcome.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-3 items-center">
          <Button variant="outline" onClick={handleTemplate} className="gap-2" data-testid="bulk-status-template-btn">
            <Download size={16} /> Download Template
          </Button>
          <CsvDownloadButton onDownload={handleTemplate} testId="bulk-status-template-csv" size="default" label="Template CSV" />
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => setFile(e.target.files[0])}
            className="text-sm"
            data-testid="bulk-status-file-input"
          />
          <Button onClick={handleUpload} disabled={!file || uploading} className="gap-2" data-testid="bulk-status-upload-btn">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {uploading ? 'Updating...' : 'Update Status'}
          </Button>
        </div>

        {result && (
          <div className="mt-4 space-y-3" data-testid="bulk-status-result">
            <div className="grid grid-cols-4 gap-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Updated</div>
                <div className="text-2xl font-bold text-emerald-900">{result.success_count}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-700 font-semibold">Already-Set</div>
                <div className="text-2xl font-bold text-slate-900">{result.skipped_count}</div>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <div className="text-xs uppercase tracking-wide text-red-700 font-semibold">Errors</div>
                <div className="text-2xl font-bold text-red-900">{result.error_count}</div>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <div className="text-xs uppercase tracking-wide text-blue-700 font-semibold">Total Rows</div>
                <div className="text-2xl font-bold text-blue-900">{result.total_rows}</div>
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2 text-xs uppercase tracking-wider text-slate-600">Row</th>
                    <th className="text-left p-2 text-xs uppercase tracking-wider text-slate-600">Product</th>
                    <th className="text-left p-2 text-xs uppercase tracking-wider text-slate-600">Transition</th>
                    <th className="text-left p-2 text-xs uppercase tracking-wider text-slate-600">Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.results || []).map((r) => (
                    <tr key={r.row_number} className="border-t border-slate-100">
                      <td className="p-2 font-mono text-xs">{r.row_number}</td>
                      <td className="p-2">
                        <div className="font-mono text-xs text-slate-700">
                          {r.resolved_product_code || r.product_id || '—'}
                        </div>
                        <div className="text-xs text-slate-500">
                          {r.resolved_product_name || r.product_name || ''}
                        </div>
                      </td>
                      <td className="p-2 text-xs">
                        {r.is_valid ? (
                          <span>
                            <span className="font-mono">{r.previous_status || '—'}</span>
                            <span className="mx-1">→</span>
                            <span className="font-mono font-semibold">{r.new_status || '—'}</span>
                          </span>
                        ) : (
                          <span className="text-red-600">—</span>
                        )}
                      </td>
                      <td className="p-2 text-xs">
                        {r.is_valid && r.changed && (
                          <span className="inline-flex items-center gap-1 text-emerald-700">
                            <CheckCircle2 size={14} /> Updated
                          </span>
                        )}
                        {r.is_valid && !r.changed && (
                          <span className="inline-flex items-center gap-1 text-slate-600">
                            <MinusCircle size={14} /> Already-set
                          </span>
                        )}
                        {!r.is_valid && (
                          <span className="inline-flex items-center gap-1 text-red-700">
                            <XCircle size={14} /> {r.error}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={close} data-testid="bulk-status-close-btn">
            <FileSpreadsheet size={16} className="mr-2" /> Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductBulkStatusDialog;
